import { type ForwardedRef, forwardRef, useEffect } from 'react';

import { type ListDivisionsQuery, type ListDivisionsQueryVariables, useListDivisionsQuery } from '@/common/graphql';

import type { ComboboxItemWithFormatted, CommonProps, UseQueryReturn } from '../props';
import { SelectSingle, type SelectSingleProps } from '../SelectSingle';

type Variables = Partial<Omit<ListDivisionsQueryVariables, 'pageNumber' | 'pageSize' | 'keyword'>>;
type Data = ComboboxItemWithFormatted &
  Omit<ListDivisionsQuery['listDivisions']['items'][number], 'id' | 'name' | 'enabled'>;
type Props = CommonProps<Variables>;

const useQuery = (
  variables: Variables | undefined,
  onLoadingCompleted?: () => void,
): UseQueryReturn<ListDivisionsQueryVariables, Data> => {
  const { data, ...rest } = useListDivisionsQuery({
    variables: {
      pageNumber: null,
      pageSize: null,
      keyword: null,
      enabled: variables?.enabled ?? null,
    },
    fetchPolicy: 'cache-first',
  });

  useEffect(() => {
    if (onLoadingCompleted) onLoadingCompleted();
  }, [onLoadingCompleted]);

  return {
    data:
      data?.listDivisions?.items?.map(it => ({
        ...it,
        value: it.id,
        label: it.name,
        disabled: !it.enabled,
      })) || [],
    ...rest,
  };
};

export const DivisionSelectSingle = forwardRef(
  (props: Props & SelectSingleProps<Data>, ref: ForwardedRef<HTMLDivElement>) => {
    const { data, loading } = useQuery(props.variables, props.onLoadingCompleted);
    return <SelectSingle ref={ref} {...props} data={data} loading={loading} />;
  },
);
